import { legalEntitiesService } from '@/api/legal-entities.service';
import type { CreateLegalEntityDto, UpdateLegalEntityDto } from '@carvia/ros-client-types';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toRef, toValue } from 'vue';

export const LEGAL_ENTITIES_QUERY_KEY = 'legalEntities';

/** All legal entities, sorted by name. Small list, cached for the session. */
export const useLegalEntities = (enabled: MaybeRefOrGetter<boolean> = true) => {
  return useQuery({
    queryKey: [LEGAL_ENTITIES_QUERY_KEY],
    queryFn: () => legalEntitiesService.getAll(),
    enabled: toRef(enabled),
    staleTime: 5 * 60_000,
  });
};

export const useLegalEntity = (
  id: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [LEGAL_ENTITIES_QUERY_KEY, toRef(id)],
    queryFn: () => legalEntitiesService.getOne(toValue(id)),
    enabled: toRef(enabled),
  });
};

export const useCreateLegalEntity = () => {
  return useMutation({
    mutationKey: [LEGAL_ENTITIES_QUERY_KEY],
    mutationFn: (legalEntity: CreateLegalEntityDto) => legalEntitiesService.create(legalEntity),
    onMutate: () => ({
      successMessage: 'Legal entity created',
      invalidateQueryKeys: [LEGAL_ENTITIES_QUERY_KEY],
    }),
  });
};

export const useUpdateLegalEntity = () => {
  return useMutation({
    mutationKey: [LEGAL_ENTITIES_QUERY_KEY],
    mutationFn: ({ id, legalEntity }: { id: string; legalEntity: UpdateLegalEntityDto }) =>
      legalEntitiesService.update(id, legalEntity),
    onMutate: () => ({
      successMessage: 'Legal entity updated',
      invalidateQueryKeys: [LEGAL_ENTITIES_QUERY_KEY],
    }),
  });
};
